import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import htmlToDraft from "html-to-draftjs";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import configVars from "../config";
import { Form, Button, Spinner, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { DELETE_BOARD_NOTE } from "../redux/types";
import ConfirmationBox from "./ConfirmationBox";
import useExitPrompt from "../utils/useExitPrompt";
import { Prompt } from "react-router-dom";
import ShowMoreText from "react-show-more-text";

const SingleNote = ({
  userid,
  note,
  editNoteIndex,
  setEditNoteIndex,
  index,
  _handleAddUpdateBoardNotes,
  token,
  boardId,
}) => {
  const [noteContent, setNoteContent] = useState("");
  const [translation] = useTranslation("translations");
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isEditError, setIsEditError] = useState(false);
  const [editErrorMsg, setEditErrorMsg] = useState("");
  const [isDeleteError, setIsDeleteError] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState("");
  const [deletingNote, setDeletingNote] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [title, setTitle] = useState(note?.title);
  const [noteContentState, setNoteContentState] = useState("");
  const [originalNoteContentState, setOriginalNoteContentState] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useExitPrompt(false);

  const dispatch = useDispatch();

  const handleShowConfirmation = () => setShowConfirmation(true);
  const handleCloseConfirmation = () => setShowConfirmation(false);
  const handleDelete = () => {
    if (parseInt( userid, 10 ) !== parseInt( note?.author_id, 10 )) {
      setIsDeleteError(true);
      setDeleteErrorMsg(translation("delete_note_error"));
      setTimeout(() => {
        setIsDeleteError(false);
        setDeleteErrorMsg("");
      }, 3000);
      return;
    } else {
      setIsDeleteError(false);
      setDeleteErrorMsg("");
      handleShowConfirmation();
    }
  };
  const handleSingleNoteEdit = () => {

    if (parseInt( userid, 10 ) !== parseInt( note?.author_id, 10 )) {
      setIsEditError(true);
      setEditErrorMsg(translation("edit_note_error"));
      setTimeout(() => {
        setIsEditError(false);
        setEditErrorMsg("");
      }, 3000);
      return;
    } else {
      setIsEditError(false);
      setEditErrorMsg("");
      setEditNoteIndex(index);
    }
  };
  const _handleChange = (value, field) => {
    if (!isBlocking) {
      setIsBlocking(true);
      setShowExitPrompt(true);
    }
    if (field === "title") {
      setTitle(value);
    } else if (field === "noteContent") {
      setNoteContent(value);
    }
  };

  const _handleEditClose = () => {
    if (isBlocking) {
      const close = window.confirm(translation("form_close_confirmation"));
      if (close === true) {
        setEditNoteIndex(-1);
        setShowExitPrompt(false);
        setIsBlocking(false);
        setTitle(note?.title);
        setNoteContentState(originalNoteContentState);
      }
    } else {
      setEditNoteIndex(-1);
      setShowExitPrompt(false);
      setIsBlocking(false);
    }
  };

  const deleteNote = async () => {
    setDeletingNote(true);
    try {
      const response = await fetch(
        `${configVars.apiUrl}sustainchange/v1/delete-board-notes`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${configVars.auth}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: token,
            id: note?.note_id,
          }),
        }
      );
      const deleteRespose = await response.json();
      if (deleteRespose?.status_code === 200) {
        dispatch({
          type: DELETE_BOARD_NOTE,
          payload: { noteId: note?.note_id, boardId: boardId },
        });
      }
    } catch (err) {}
    setDeletingNote(false);
    handleCloseConfirmation();
  };

  useEffect(() => {
    if (editNoteIndex === index) {
      let noteContent = note?.content;

      if (noteContent) {
        const contentBlock1 = htmlToDraft(noteContent);

        if (contentBlock1) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock1.contentBlocks
          );
          const editorState = EditorState.createWithContent(contentState);
          setNoteContent(editorState);
          let _contentState = convertToRaw(editorState.getCurrentContent());
          setNoteContentState(_contentState);
          setOriginalNoteContentState(_contentState);
        }
      } else {
        const contentBlock1 = htmlToDraft("");
        const contentState = ContentState.createFromBlockArray(
          contentBlock1.contentBlocks
        );
        const editorState = EditorState.createWithContent(contentState);
        setNoteContent(editorState);
        let _contentState = convertToRaw(editorState.getCurrentContent());
        setNoteContentState(_contentState);
      }
      setTitle(note?.title);
    }
  }, [note, editNoteIndex, index]);

  return editNoteIndex === index ? (
    <div className="media mb-3 w-100 border p-3 rounded-3 board-single-note-wrapper show-item-on-hover-wrapper">
      <Prompt
        when={showExitPrompt}
        message={translation("form_close_confirmation")}
      />
      <Form.Group className="mb-3">
        <Form.Label>
          <b>{translation("title")}</b>
        </Form.Label>
        <Form.Control
          type="text"
          defaultValue={note?.title?.replaceAll("&amp;", "&")}
          onChange={(e) => _handleChange(e.target.value, "title")}
          style={{ fontSize: "14px", padding: "0.5rem 0.5rem" }}
        />
      </Form.Group>
      <Editor
        toolbarOnFocus
        toolbar={configVars?.wysiwygToolbarOptions}
        editorState={noteContent}
        toolbarClassName="toolbarClassName"
        wrapperClassName="wrapperClassName"
        editorClassName="editor-wrapper"
        onEditorStateChange={(e) => _handleChange(e, "noteContent")}
        onChange={(e) => {
          setNoteContentState(e);
        }}
      />

      <div className="row mt-2">
        <div className="col-6">
          {isError && <span className="text-danger">{errorMsg}</span>}
        </div>
        <div className="col-6 edit-single-note">
          <Button
            onClick={() => {
              if (title?.length === 0 || title === null) {
                setIsError(true);
                setErrorMsg(translation("title_error"));
                return;
              }
              setIsError(false);
              setErrorMsg("");
              _handleAddUpdateBoardNotes({
                note_id: note?.note_id,
                title: title,
                description: noteContentState,
                loadingCallback: (val) => {
                  setIsLoading(val);
                },
              });
            }}
            disabled={isLoading}
            className="text-white rounded btn btn-halite-blue-light btn-sm pull-right"
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" style={{ color: "#fff" }} />
            ) : (
              <>{translation("buttons.save")}</>
            )}
          </Button>
          <Button
            type="button"
            className="text-white close-btn text-bold btn btn-sm pull-right text-decoration-none py-1 px-2 mx-2 visible-on-hover-item weight-bold btn-halite-blue-light"
            onClick={_handleEditClose}
          >
            {translation("cancel")}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="media mb-3 w-100 border p-3 rounded-3 board-single-note-wrapper show-item-on-hover-wrapper">
      <Row>
        <Col lg="3" className="px-0 text-center">
          <div>
            <div className="profile-nav-text d-inline-grid align-items-center mx-3 ">
              <div className="text-align-center">
                <div className="profile-nav-warp bg-primary rounded-circle d-inline-flex"></div>
              </div>
              <div className="my-2">
                <div className="note-author h5 mb-0 text-capitalize font-weight-normal">
                  {note?.author}
                </div>
                <div
                  className="my-1 text-uppercase note-date fw-bold h5 mb-0 font-weight-normal"
                  style={{ color: "#0000005c", fontSize: "12px" }}
                >
                  {note?.date}
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col lg="9">
          <div className="media-body">
            <strong className="mt-0 note-heading">{title}</strong>
            <ShowMoreText
              /* Default options */
              lines={3}
              more={translation("show_more")}
              less={translation("show_less")}
              className="note-paragraph"
              anchorClass="my-anchor-css-class"
              expanded={false}
              truncatedEndingComponent={"... "}
            >
              <div dangerouslySetInnerHTML={{ __html: note?.content }} />
            </ShowMoreText>
          </div>
        </Col>
      </Row>

      <div className="row">
        <div className="col-md-6 mt-3" style={{ paddingLeft: "76px" }}>
          {isDeleteError ? (
            <span className="text-danger">{deleteErrorMsg}</span>
          ) : isEditError ? (
            <span className="text-danger">{editErrorMsg}</span>
          ) : (
            ""
          )}
        </div>
        <div className="col-md-6">
          <button
            type="button"
            // onClick={handleShowConfirmation}
            onClick={handleDelete}
            className="btn btn-link pull-right px-0 text-decoration-none text-sm visible-on-hover-item text-danger"
          >
            {deletingNote ? (
              <Spinner animation="border" variant="primary" size="sm" />
            ) : (
              translation("delete_note")
            )}
          </button>

          <button
            type="button"
            className="btn btn-link pull-right text-decoration-none text-sm px-2 visible-on-hover-item text-muted"
            // onClick={() => {
            //   // setEditNoteIndex(index);
            // }}
            onClick={handleSingleNoteEdit}
          >
            {translation("edit_note")}
          </button>
        </div>
      </div>
      <ConfirmationBox
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        title={translation("delete_note_title")}
        message={translation("delete_note_warning")}
        confirmationAction={deleteNote}
        loading={deletingNote}
        size="sm"
      />
    </div>
  );
};
export default SingleNote;
